// ============================================================================
// CHANGO V5.0 — D4 Ops SKU Pricing console
// ============================================================================
// Lists every SKU with its current amount (filled / unfilled flag) and lets
// an admin file a price-change proposal. The page never sets a price
// directly — it only enqueues a review_request via:
//   POST /api/ops/sku-pricing/:skuCode/propose  { amount_rmb, reason }
// The proposal then appears in /ops/review-requests for a peer to approve.
//
// §20 #19: prices are NEVER hardcoded on the client. The numeric input is
// bound to ¥/cents conversion only at the input layer; transport is the raw
// decimal string.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import {
  brandForSku, sortByBrand, A_SERIES, B_SERIES, SERIES_COLORS
} from '../../lib/skuBrandVersion.js';

const MIN_PRICE = 0.01;
const MAX_PRICE = 9_999_999.99;
const REASON_MIN = 8;
const REASON_MAX = 1000;

function formatYuan(v) {
  if (v == null) return <span className="text-amber-400">未填</span>;
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return (
    <span className="font-semibold tabular-nums">
      ¥{n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
}

function ProposeDialog({ open, sku, onClose, onSubmitted }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setAmount('');
      setReason('');
      setBusy(false);
      setErr(null);
    }
  }, [open, sku?.sku_code]);

  if (!open || !sku) return null;

  const num = Number.parseFloat(amount);
  const numOk = Number.isFinite(num) && num >= MIN_PRICE && num <= MAX_PRICE;
  const reasonTrim = reason.trim();
  const reasonOk = reasonTrim.length >= REASON_MIN && reasonTrim.length <= REASON_MAX;
  const formOk = numOk && reasonOk && !busy;

  const submit = async () => {
    if (!formOk) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post(`/api/ops/sku-pricing/${encodeURIComponent(sku.sku_code)}/propose`, {
        amount_rmb: num,
        reason: reasonTrim
      });
      onSubmitted?.(res);
      onClose();
    } catch (e) {
      setErr(e.message || '提交失败');
    } finally {
      setBusy(false);
    }
  };

  const brandInfo = brandForSku(sku.sku_code);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/70" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-base font-semibold text-slate-800">提交价格变更</h3>
        <p className="mt-1 text-sm font-semibold text-slate-700">
          {brandInfo.fullName}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          SKU <code className="rounded bg-slate-100 px-1">{sku.sku_code}</code>
        </p>
        <p className="mt-2 text-xs text-slate-500">
          当前价格：{sku.amount_rmb == null ? '未填' : `¥${Number(sku.amount_rmb).toLocaleString('zh-CN')}`}
        </p>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-700">建议价格 (RMB)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min={MIN_PRICE}
            max={MAX_PRICE}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-chango-500 focus:outline-none"
            placeholder="例如 9800.00"
          />
          {!numOk && amount !== '' && (
            <span className="mt-1 block text-xs text-rose-600">
              请输入 {MIN_PRICE} – {MAX_PRICE} 之间的金额
            </span>
          )}
        </label>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-700">变更理由</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={REASON_MAX}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-chango-500 focus:outline-none"
            placeholder="为什么要调整？影响范围？已与谁同步？"
          />
          <span className="mt-1 flex justify-between text-xs text-slate-400">
            <span>{reasonTrim.length}/{REASON_MAX}</span>
            {reasonTrim.length > 0 && !reasonOk && (
              <span className="text-rose-500">至少 {REASON_MIN} 个字符</span>
            )}
          </span>
        </label>

        {err && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {err}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!formOk}
            className="rounded-md bg-chango-600 px-4 py-2 text-sm font-semibold text-white hover:bg-chango-700 disabled:opacity-50"
          >
            {busy ? '提交中…' : '提交申请'}
          </button>
        </div>
      </div>
    </div>
  );
}

function useSkus() {
  const [state, setState] = useState({ items: [], unfilled: 0, total: 0, loading: true, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await api.get('/api/ops/sku-pricing');
        if (cancelled) return;
        setState({
          items: Array.isArray(res?.items) ? res.items : [],
          unfilled: Number.isInteger(res?.unfilled_count) ? res.unfilled_count : 0,
          total: Number.isInteger(res?.total) ? res.total : 0,
          loading: false,
          error: null
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setState({ items: [], unfilled: 0, total: 0, loading: false, error: null });
        } else {
          setState((s) => ({ ...s, loading: false, error: err.message || '加载失败' }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  return { ...state, reload };
}

export default function OpsSkuPricing() {
  const { user } = useAuth();
  const { items, unfilled, total, loading, error, reload } = useSkus();
  const [proposeRow, setProposeRow] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  // §9.3 排序: A1-A5 → B1-B4 → 加购
  const sortedItems = useMemo(() => sortByBrand(items, (s) => s.sku_code), [items]);

  // 把 sortedItems 按 series ('A' / 'B' / '_') 切片, 加分组表头。
  // §9.5.1 颜色规范: A 系蓝 / B 系绿 / 加购橙
  const groupedItems = useMemo(() => {
    const aRows = [];
    const bRows = [];
    const addonRows = [];
    for (const s of sortedItems) {
      const info = brandForSku(s.sku_code);
      if (info.series === 'A') aRows.push(s);
      else if (info.series === 'B') bRows.push(s);
      else addonRows.push(s);
    }
    const out = [];
    if (aRows.length) out.push({ series: 'A', label: 'A 系（含工厂模块 A）', rows: aRows });
    if (bRows.length) out.push({ series: 'B', label: 'B 系（仅外贸模块 B，无工厂）', rows: bRows });
    if (addonRows.length) out.push({ series: '_', label: '加购', rows: addonRows });
    return out;
  }, [sortedItems]);

  const onSubmitted = (res) => {
    setOkMsg(`已提交 review_request #${res?.review_request_id} (待批准)`);
    reload();
    setTimeout(() => setOkMsg(null), 6000);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">SKU 价格管理</h1>
            <p className="text-xs text-slate-400">
              {user?.email} · {user?.role} · 修改任何价格都会进入 <Link to="/ops/review-requests" className="underline">审批队列</Link>
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700"
          >
            刷新
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded border border-slate-700 bg-slate-800/40 p-3">
            <div className="text-xs text-slate-400">SKU 总数</div>
            <div className="mt-1 text-xl font-semibold">{total}</div>
          </div>
          <div className="rounded border border-amber-700/60 bg-amber-900/20 p-3">
            <div className="text-xs text-amber-300">未定价</div>
            <div className="mt-1 text-xl font-semibold text-amber-200">{unfilled}</div>
          </div>
          <div className="rounded border border-emerald-700/60 bg-emerald-900/20 p-3">
            <div className="text-xs text-emerald-300">已定价</div>
            <div className="mt-1 text-xl font-semibold text-emerald-200">{total - unfilled}</div>
          </div>
        </div>

        {(error || okMsg) && (
          <div className={[
            'mb-4 rounded-md border px-4 py-2 text-sm',
            error
              ? 'border-rose-500/40 bg-rose-900/30 text-rose-200'
              : 'border-emerald-500/40 bg-emerald-900/30 text-emerald-200'
          ].join(' ')}>
            {error || okMsg}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3">版本 / 套餐</th>
                <th className="px-4 py-3">SKU 代码</th>
                <th className="px-4 py-3">当前价格</th>
                <th className="px-4 py-3">计费周期</th>
                <th className="px-4 py-3">工作区</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">加载中…</td></tr>
              )}
              {!loading && sortedItems.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">尚无 SKU 行</td></tr>
              )}
              {!loading && groupedItems.map((group) => (
                <React.Fragment key={group.series}>
                  <tr className={
                    group.series === 'A' ? 'bg-blue-950/40'
                    : group.series === 'B' ? 'bg-emerald-950/40'
                    : 'bg-amber-950/40'
                  }>
                    <td colSpan={6} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider">
                      <span className={
                        group.series === 'A' ? 'text-blue-300'
                        : group.series === 'B' ? 'text-emerald-300'
                        : 'text-amber-300'
                      }>{group.label}</span>
                    </td>
                  </tr>
                  {group.rows.map((s) => {
                    const info = brandForSku(s.sku_code);
                    const dotColor = group.series === 'A' ? 'bg-blue-500'
                      : group.series === 'B' ? 'bg-emerald-500' : 'bg-amber-500';
                    const original = s.original_amount_rmb;
                    const hasPromo = original != null && Number(original) > Number(s.amount_rmb ?? 0);
                    return (
                      <tr key={s.sku_code} className="border-t border-slate-700/60 hover:bg-slate-700/20">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${dotColor}`} aria-hidden="true" />
                            <span className="font-semibold text-slate-100">{info.shortName}</span>
                          </div>
                          <div className="mt-0.5 ml-4 text-[11px] text-slate-400">
                            模块: {info.layers.join(' + ') || '—'} · {info.hasELayer ? '含运营主体层' : '单主体'}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-400">{s.sku_code}</td>
                        <td className="px-4 py-3">
                          {hasPromo && (
                            <div className="text-xs text-slate-500 line-through">
                              {`¥${Number(original).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </div>
                          )}
                          <div className={hasPromo ? 'text-rose-400' : ''}>
                            {formatYuan(s.amount_rmb)}
                          </div>
                          {hasPromo && (
                            <span className="mt-1 inline-block rounded bg-rose-900/40 px-1.5 py-0.5 text-[10px] font-medium text-rose-300">
                              限时特价
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-400">{s.billing_cycle || '—'}</td>
                        <td className="px-4 py-3 text-slate-400">单工作区</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setProposeRow(s)}
                            className="rounded bg-chango-600 px-3 py-1 text-xs font-semibold text-white hover:bg-chango-700"
                          >
                            提交变更
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          §20 #19 红线：本页不存储也不渲染任何硬编码价格；提交的金额仅在审批通过、由另一位 admin 执行 apply 后才会写入 sku_pricing.
        </p>
      </main>

      <ProposeDialog
        open={Boolean(proposeRow)}
        sku={proposeRow}
        onClose={() => setProposeRow(null)}
        onSubmitted={onSubmitted}
      />
    </div>
  );
}
